import { RefObject } from 'react';

import type { HotkeyBase, ModifierType } from './hotkeys/types.ts';

const collectModifierNeeds = (
  first: ModifierType | undefined,
  second: ModifierType | undefined
) => {
  let needShift = false;
  let needAlt = false;
  let needCtrl = false;
  let needMeta = false;
  const add = (m: ModifierType | undefined) => {
    if (m === 'Shift') needShift = true;
    else if (m === 'Alt' || m === 'Option') needAlt = true;
    else if (m === 'Ctrl') needCtrl = true;
    else if (m === 'Meta' || m === 'Cmd') needMeta = true;
  };
  add(first);
  add(second);
  return { needAlt, needCtrl, needMeta, needShift };
};

/** `true` when `ev` matches both optional modifier slots (e.g. Alt alone vs Shift+Alt). No extra modifiers. */
export const keyboardModifiersMatchHotkeyBase = (
  ev: KeyboardEvent,
  hotkey: Pick<HotkeyBase, 'hotkeyModifier' | 'secondaryHotkeyModifier'>
): boolean => {
  const { needAlt, needCtrl, needMeta, needShift } = collectModifierNeeds(
    hotkey.hotkeyModifier?.modifier,
    hotkey.secondaryHotkeyModifier?.modifier
  );
  if (needMeta) {
    // Platform command key: Cmd on macOS, Ctrl elsewhere; either satisfies it.
    return (ev.metaKey || ev.ctrlKey) && ev.shiftKey === needShift && ev.altKey === needAlt;
  }
  if (ev.metaKey) return false;
  return ev.shiftKey === needShift && ev.altKey === needAlt && ev.ctrlKey === needCtrl;
};

/** `true` when `code` is the primary key or the optional alternate (e.g. arrow vs WASD). */
export const hotkeyPrimaryOrOptionalCodeMatches = (hotkey: HotkeyBase, code: string): boolean =>
  hotkey.primary.code === code || hotkey.optionalPrimary?.code === code;

export const isInside = (scopeRef: RefObject<HTMLElement | null>) => {
  const scope = scopeRef.current;
  if (!scope) return false;
  const active = document.activeElement;
  // Before any focus move, activeElement is often body/html — still treat as in-scope so
  // shortcuts work without clicking the window first
  if (!active || active === document.body || active === document.documentElement) {
    return true;
  }
  return active === scope || scope.contains(active);
};

/**
 * Like `isInside`, but also treats focus on an **ancestor** of the scope as in-scope. Clicking
 * empty page chrome moves focus to a wrapping element (e.g. the `tabIndex={-1}`
 * `GlobalHotkeysFocusShell`) that contains the page but isn't contained by it. Portaled overlays
 * (modals) are neither ancestors nor descendants of the scope, so they stay excluded.
 */
export const isInsideOrAroundScope = (scopeRef: RefObject<HTMLElement | null>): boolean => {
  const scope = scopeRef.current;
  if (!scope) return false;
  const active = document.activeElement;
  if (!active || active === document.body || active === document.documentElement) return true;
  return scope.contains(active) || active.contains(scope);
};

export const isTypingTarget = (el: EventTarget | null): boolean => {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || el.isContentEditable || tag === 'select';
};

/** Keys a focused menu or menu button uses itself (open, move between items, close). */
const MENU_NAVIGATION_KEYS = new Set([
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'End',
  'Enter',
  'Escape',
  'Home',
  ' ',
]);

/**
 * True when a navigation key is pressed on a menu button or inside a menu, so the menu handles
 * it instead of, for example, the camera hotkeys panning the camera.
 */
export const isMenuNavigationKeyEvent = (ev: KeyboardEvent): boolean => {
  if (!(ev.target instanceof HTMLElement)) return false;
  if (!MENU_NAVIGATION_KEYS.has(ev.key)) return false;
  return ev.target.closest('[role="menu"], [aria-haspopup="menu"]') !== null;
};

export const clearTimeoutFromRef = (
  timeoutRef: RefObject<ReturnType<typeof setTimeout> | undefined>
) => {
  if (timeoutRef.current !== undefined) {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = undefined;
  }
};
